import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import { StripeSubscriptionPayment } from "./StripeSubscriptionPayment";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import {
  CheckCircle, XCircle, ArrowRight, Info, Signature, Mail, Shield
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PublicSubscriptionPage = () => {
  const { slug } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('view');
  const [signatureData, setSignatureData] = useState({
    full_name: '', email: '', position: '', agreed: false, identity_confirmed: false
  });
  const [signing, setSigning] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchSubscription(); }, [slug]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/${slug}`);
      setSubscription(response.data.subscription);
      setBusiness(response.data.business);
      const sub = response.data.subscription;
      if (sub.signature_pending) { setStep('verify'); setVerificationSent(true); }
      else if (sub.contract_agreed && sub.status !== 'active') { setStep('payment'); }
      else if (sub.status === 'active') { setStep('success'); }
    } catch (err) {
      setError(err.response?.data?.detail || "Subscription not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let interval;
    if (verificationSent && step === 'verify') {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/subscription/${slug}`);
          const sub = response.data.subscription;
          if (sub.contract_agreed && !sub.signature_pending) {
            setSubscription(sub);
            setStep('payment');
            setVerificationSent(false);
            toast({ title: "Signature Verified!", description: "Your signature has been verified. Now set up your payment method." });
          }
        } catch (e) { /* continue polling */ }
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [verificationSent, step, slug, toast]);

  const handleSign = async () => {
    if (!signatureData.full_name || !signatureData.email) {
      toast({ title: "Required Fields Missing", description: "Please enter your full name and email address.", variant: "destructive" });
      return;
    }
    if (!signatureData.agreed || !signatureData.identity_confirmed) {
      toast({ title: "Confirmations Required", description: "Please confirm your identity and agree to the terms.", variant: "destructive" });
      return;
    }
    setSigning(true);
    try {
      const response = await axios.post(`${API}/subscription/${slug}/sign`, {
        full_name: signatureData.full_name, email: signatureData.email,
        position: signatureData.position, agreed: signatureData.agreed,
        identity_confirmed: signatureData.identity_confirmed, user_agent: navigator.userAgent
      });
      if (response.data.verification_pending) {
        setVerificationSent(true);
        setStep('verify');
        toast({ title: "Verification Email Sent", description: `Please check ${signatureData.email} and click the verification link.` });
      } else {
        setStep('payment');
        fetchSubscription();
      }
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to sign contract", variant: "destructive" });
    } finally {
      setSigning(false);
    }
  };

  const handleClientCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel this subscription?")) return;
    try {
      await axios.post(`${API}/subscription/${slug}/cancel`);
      toast({ title: "Subscription Cancelled", description: "Your subscription has been cancelled successfully." });
      fetchSubscription();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed to cancel subscription", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e6007e] mx-auto"></div>
          <p className="mt-4 text-gray-600 font-body">Loading subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Subscription Not Found</h2>
            <p className="text-gray-600 font-body">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (subscription.status === 'cancelled') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2 font-heading">Subscription Cancelled</h2>
            <p className="text-gray-600 font-body">This subscription has been cancelled. Contact {business?.name || 'the business'} if you'd like to resubscribe.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          {business?.logo_url ? (
            <img src={business.logo_url} alt={business.name} className="h-12 mx-auto mb-4" />
          ) : (
            <h1 className="text-2xl font-bold text-gray-900 mb-2 font-heading">{business?.name || 'Paify'}</h1>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'view' ? 'bg-[#e6007e] text-white' : 'bg-green-500 text-white'}`}>1</div>
            <span className="text-sm text-gray-600 font-body">Review</span>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'sign' || step === 'verify' ? 'bg-[#e6007e] text-white' : step === 'payment' || step === 'success' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>2</div>
            <span className="text-sm text-gray-600 font-body">Sign</span>
            <div className="w-8 h-0.5 bg-gray-300"></div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step === 'payment' ? 'bg-[#e6007e] text-white' : step === 'success' ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>3</div>
            <span className="text-sm text-gray-600 font-body">Pay</span>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 md:p-8">
            {step === 'view' && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">{subscription.title}</h2>
                {subscription.description && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 font-heading">Scope of Work</h4>
                    <p className="text-gray-600 font-body whitespace-pre-wrap">{subscription.description}</p>
                  </div>
                )}
                <div className="border-t border-b py-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-body">Monthly Amount</span>
                    <span className="text-3xl font-bold text-[#e6007e] font-heading">&pound;{subscription.amount?.toFixed(2)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 font-body">Billed monthly on the same day each month</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800 font-body">
                      <p className="font-semibold mb-1">Flexible Subscription</p>
                      <p>You can cancel this subscription at any time. No long-term commitment required.</p>
                    </div>
                  </div>
                </div>
                <Button onClick={() => setStep('sign')} className="w-full bg-[#e6007e] hover:bg-[#c90068] text-white font-body" data-testid="proceed-to-sign-btn">
                  Continue to Sign Contract<ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {step === 'sign' && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Sign Contract</h2>
                <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-48 overflow-y-auto">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 font-heading">Service Agreement</h4>
                  <div className="text-sm text-gray-600 font-body space-y-2">
                    <p>This Agreement is entered into between <strong>{business?.name}</strong> ("Service Provider") and <strong>{subscription.client_name}</strong> ("Client").</p>
                    <p><strong>Services:</strong> {subscription.title}</p>
                    <p><strong>Payment:</strong> &pound;{subscription.amount?.toFixed(2)}/month, billed automatically.</p>
                    <p><strong>Cancellation:</strong> Either party may cancel at any time.</p>
                  </div>
                </div>
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="font-body text-sm">Full Name *</Label>
                      <Input value={signatureData.full_name} onChange={(e) => setSignatureData({...signatureData, full_name: e.target.value})} placeholder={subscription.client_name} className="font-body" data-testid="signature-name-input" />
                    </div>
                    <div>
                      <Label className="font-body text-sm">Email Address *</Label>
                      <Input type="email" value={signatureData.email} onChange={(e) => setSignatureData({...signatureData, email: e.target.value})} placeholder={subscription.client_email} className="font-body" data-testid="signature-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="font-body text-sm">Position/Title *</Label>
                    <Input value={signatureData.position} onChange={(e) => setSignatureData({...signatureData, position: e.target.value})} placeholder="e.g., Director, Owner, Manager" className="font-body" data-testid="signature-position-input" />
                    <p className="text-xs text-gray-500 mt-1 font-body">Required for UK government contract compliance</p>
                  </div>
                  <div className="space-y-3 border-t pt-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={signatureData.identity_confirmed} onChange={(e) => setSignatureData({...signatureData, identity_confirmed: e.target.checked})} className="mt-1 w-4 h-4 text-[#e6007e] rounded" />
                      <span className="text-sm text-gray-700 font-body"><strong>I confirm my identity</strong> - I am {subscription.client_name} or authorized to sign on their behalf.</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={signatureData.agreed} onChange={(e) => setSignatureData({...signatureData, agreed: e.target.checked})} className="mt-1 w-4 h-4 text-[#e6007e] rounded" />
                      <span className="text-sm text-gray-700 font-body"><strong>I agree to the terms</strong> - I understand my electronic signature is legally binding under UK Electronic Communications Act 2000.</span>
                    </label>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setStep('view')} className="font-body">Back</Button>
                  <Button onClick={handleSign} disabled={signing || !signatureData.full_name || !signatureData.email || !signatureData.position || !signatureData.agreed || !signatureData.identity_confirmed} className="flex-1 bg-[#e6007e] hover:bg-[#c90068] text-white font-body disabled:opacity-50" data-testid="sign-contract-btn">
                    {signing ? 'Processing...' : 'Sign & Send Verification'}<Signature className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-4 text-center font-body">A verification email will be sent to confirm your signature.</p>
              </>
            )}

            {step === 'verify' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Check Your Email</h2>
                <p className="text-gray-600 mb-6 font-body">We've sent a verification link to:<br/><strong>{subscription.contract_signer_email || signatureData.email}</strong></p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-semibold text-amber-800 mb-2 font-heading">What to do:</h4>
                  <ol className="list-decimal list-inside text-sm text-amber-700 space-y-1 font-body">
                    <li>Check your inbox (and spam folder)</li>
                    <li>Click the verification link in the email</li>
                    <li>Return here to set up payment</li>
                  </ol>
                </div>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 font-body">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#e6007e]"></div>
                  Waiting for verification...
                </div>
              </div>
            )}

            {step === 'payment' && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 font-heading">Set Up Payment</h2>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-semibold font-body">Contract Signed & Verified</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1 font-body">
                    Signed by {subscription.contract_signer_name || subscription.contract_signature} on {new Date(subscription.contract_signed_at).toLocaleDateString()}
                  </p>
                  {subscription.signature_audit_trail?.signer_position && (
                    <p className="text-xs text-green-600 mt-1 font-body">Position: {subscription.signature_audit_trail.signer_position}</p>
                  )}
                </div>
                <div className="border rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-600 font-body">Subscription</span>
                    <span className="font-semibold font-heading">{subscription.title}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-body">Monthly Amount</span>
                    <span className="text-xl font-bold text-[#e6007e] font-heading">&pound;{subscription.amount?.toFixed(2)}/mo</span>
                  </div>
                </div>
                <StripeSubscriptionPayment subscription={subscription} slug={slug} onSuccess={() => { setStep('success'); fetchSubscription(); }} />
              </>
            )}

            {step === 'success' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2 font-heading">Subscription Active!</h2>
                <p className="text-gray-600 mb-6 font-body">Your subscription to {subscription.title} is now active.</p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600 font-body">Amount</span>
                    <span className="font-semibold font-heading">&pound;{subscription.amount?.toFixed(2)}/month</span>
                  </div>
                  {subscription.next_billing_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 font-body">Next billing</span>
                      <span className="font-semibold font-heading">{new Date(subscription.next_billing_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                <Button variant="outline" onClick={handleClientCancel} className="text-red-600 hover:text-red-700 font-body">Cancel Subscription</Button>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-8 font-body">
          Powered by <span className="font-semibold">Paify</span> &bull; Secure payments by Stripe
        </p>
      </div>
    </div>
  );
};

export default PublicSubscriptionPage;
